import { createEffect, onCleanup } from 'solid-js';

interface KeyboardShortcut {
	key: string;
	action: () => void;
	description?: string;
	/** Modifier keys required (ctrl, alt, shift, meta) */
	modifiers?: {
		ctrl?: boolean;
		alt?: boolean;
		shift?: boolean;
		meta?: boolean;
	};
}

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
	createEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const matchingShortcut = shortcuts.find((shortcut) => {
				// Check if key matches
				const keyMatches = shortcut.key.toLowerCase() === event.key.toLowerCase();
				if (!keyMatches) return false;

				// Check modifiers if specified
				if (shortcut.modifiers) {
					const { ctrl, alt, shift, meta } = shortcut.modifiers;

					// Check each modifier - if specified, it must match event state
					if (ctrl !== undefined && event.ctrlKey !== ctrl) return false;
					if (alt !== undefined && event.altKey !== alt) return false;
					if (shift !== undefined && event.shiftKey !== shift) return false;
					if (meta !== undefined && event.metaKey !== meta) return false;
				} else {
					// If no modifiers specified, ensure no modifiers are pressed
					if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) {
						return false;
					}
				}

				return true;
			});

			if (matchingShortcut) {
				// Don't trigger shortcuts when typing in input fields
				if (
					document.activeElement instanceof HTMLInputElement ||
					document.activeElement instanceof HTMLTextAreaElement
				) {
					// Allow Enter key to be captured anyway
					if (event.key !== 'Enter') {
						return;
					}
				}

				matchingShortcut.action();
				event.preventDefault();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		onCleanup(() => {
			window.removeEventListener('keydown', handleKeyDown);
		});
	});
};
